import SemaphoreLike from '../../async/SemaphoreLike';

import CompositeNodeBase from './CompositeNodeBase';

/**
 * Base building block for non-leaf nodes of composite hierarchies that conform
 * to the {@link CompositeNodeBase} interface. This implementation stores its
 * children in an array.
 */
abstract class CompositeArrayNodeBase<
  T extends CompositeNodeBase<any>,
> implements CompositeNodeBase<T> {
  private readonly children: T[] = [];
  private parent: T | null = null;
  private readonly iteratorGuard = new SemaphoreLike();

  public hasChildren(): boolean {
    return this.children.length > 0;
  }

  public getChildrenCount(): number {
    return this.children.length;
  }

  public *getChildren(): IterableIterator<T> {
    this.iteratorGuard.acquire();

    for (const child of this.children) {
      yield child;
    }

    this.iteratorGuard.release();
  }

  public getChildrenCopy(): T[] {
    return this.children.slice();
  }

  public addChild(child: T): void {
    this.ensureMutationIsSafe('addChildUnchecked');
    this.children.push(child);
    child.setParent(this);
  }

  public addChildUnchecked(child: T): void {
    this.children.push(child);
  }

  public getParent(): T | null {
    return this.parent;
  }

  public setParent(parent: T | null): void {
    this.parent = parent;
  }

  private ensureMutationIsSafe(unsafeMethodName: string) {
    if (this.iteratorGuard.isAcquired()) {
      throw new Error(
        'Attempted to modify children count while there are unconsumed ' +
          'iterators, which is probably a bug. Consider iterating over ' +
          'getChildrenCopy(), or, if you really mean it, using ' +
          unsafeMethodName
      );
    }
  }
}

export default CompositeArrayNodeBase;
